import React, { useLayoutEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './LearningPages.css';

export function LearningNav() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    // All marketing pages share this navigation; preserve explicit section links.
    if (!hash) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return <nav className="learning-nav tm-shell" aria-label="Explore learning">
    <NavLink to="/" end>Overview</NavLink>
    <NavLink to="/subjects">Subjects & levels</NavLink>
    <NavLink to="/11-plus">11+</NavLink>
    <NavLink to="/gcse">GCSE</NavLink>
    <NavLink to="/educators">For Educators</NavLink>
  </nav>;
}

export interface LearningChatMessage {
  role: 'tutor' | 'student';
  content: string;
}

interface LearningHeroProps {
  title: string;
  description: string;
  children: React.ReactNode;
  lessonTitle: string;
  messages: readonly LearningChatMessage[];
}

export function LearningHero({ title, description, children, lessonTitle, messages }: LearningHeroProps) {
  return <section className="learning-hero tm-shell">
    <div className="learning-hero-copy">

      <h1>{title}</h1>
      <p>{description}</p>
      <div className="learning-actions">{children}</div>
    </div>
    <aside className="learning-example" aria-label="Illustrative AI tutor conversation">
      <div className="learning-example-heading">
        <img src="/logo.webp" alt="" width="36" height="36" />
        <div><strong>A little help, then your turn</strong><span>Example lesson · {lessonTitle}</span></div>
      </div>
      {messages.map((message, index) => (
        <div className={`learning-message${message.role === 'student' ? ' learning-message--student' : ''}`} key={index}>
          <strong>{message.role === 'student' ? 'You' : 'AI tutor'}</strong>
          <p>{message.content}</p>
        </div>
      ))}
      <p className="learning-example-note">Ask a question, try an answer, or ask for another explanation.</p>
    </aside>
  </section>;
}

export function HowItWorks() {
  return <section className="learning-section tm-shell" id="how-it-works">
    <ol className="learning-steps">
      <li><span className="learning-step-number">01</span><h3>Choose what to work on</h3><p>Pick the course that suits you level, and then choose a prepared lesson. No need to write a prompt or wait for us to plan a lesson.</p></li>
      <li><span className="learning-step-number">02</span><h3>Learn by taking part</h3><p>The tutor will guide you through the lesson. You can always ask for a hint or a different explanation. Type or dictate your answers and questions. </p></li>
      <li><span className="learning-step-number">03</span><h3>Practise and come back</h3><p>Work through lessons, then use the practice tests and flashcards available for revision. We will keep track of your progress.</p></li>
    </ol>
  </section>;
}

export function LearningFooter() {
  return <footer className="learning-footer tm-shell"><Link to="/">TeachMeNow</Link><span>AI tutoring for independent study and supported learning.</span><a className="learning-text-link" href="mailto:contact@teachmenow.ai">Contact us</a><span>© {new Date().getFullYear()} TeachMeNow</span></footer>;
}
